import React from 'react';
import { useParams } from 'react-router-dom';
import { Phone, CheckCircle, Star, Shield, TreePine, Scissors, Zap, MapPin } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';
import { SERVICE_AREAS, getServiceArea } from '../serviceAreas';
import reviewData from '../reviews.json';

const SERVICES = [
  { icon: TreePine, title: 'Tree Removal', copy: 'Hazardous, dead or in-the-way trees taken down in sections.', href: '/tree-removal' },
  { icon: Scissors, title: 'Trimming & Pruning', copy: 'Crown reduction, deadwood removal and clearance off the roof.', href: '/tree-trimming' },
  { icon: Zap, title: 'Storm Damage', copy: '24/7 response for fallen trees, hangers and blocked access.', href: '/emergency-tree-service' },
  { icon: MapPin, title: 'Stump Grinding & Clearing', copy: 'Stumps ground out and lots cleared, cleaned up properly.', href: '/stump-grinding' }
];

const ServiceAreaPage = () => {
  const { slug } = useParams();
  const area = getServiceArea(slug) || SERVICE_AREAS[0];
  const { QuoteModal, openQuoteModal } = useQuoteModal();

  usePageMeta(area.metaTitle, area.metaDescription);

  const nearby = area.nearby.map(getServiceArea).filter(Boolean);
  const review = reviewData.reviews.find(r => r.text.length >= 60 && !r.text.trimEnd().endsWith('...'));

  return (
    <div className="min-h-screen bg-white">
      {/* No nav item corresponds to a town page, so nothing should highlight */}
      <Header currentPage="service-area" />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="w-4 h-4 mr-2" />
            {area.county} · Licensed &amp; Insured
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {area.heroLine}
          </h1>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {area.intro}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14432080747"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center space-x-2 shadow-lg transition-colors duration-200"
            >
              <Phone className="w-5 h-5" />
              <span>Call (443) 208-0747</span>
            </a>
            <button
              onClick={() => openQuoteModal()}
              className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Get a Free Quote
            </button>
          </div>

          <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-8">
            <div className="flex items-center space-x-1">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="font-medium">{reviewData.rating.toFixed(1)}/5 from {reviewData.count} reviews</span>
            </div>
            <div className="flex items-center space-x-1">
              <Shield className="w-4 h-4 text-green-600" />
              <span>Fully Insured</span>
            </div>
          </div>
        </div>
      </section>

      {/* What the work looks like here */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            What Tree Work Looks Like in {area.name}
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Every town we work in has its own quirks. Here's what shapes the job on your street.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {area.notes.map((note, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-green-600 text-white flex items-center justify-center font-display text-lg mb-4">
                  {i + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{note.title}</h3>
                <p className="text-gray-600 leading-relaxed">{note.body}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-10">
            <span className="font-semibold text-gray-900">Trees we work on most here:</span>{' '}
            {area.species}
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Services in {area.name}
          </h2>

          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICES.map(service => (
              <a
                key={service.title}
                href={service.href}
                className="bg-white rounded-xl p-6 shadow-sm flex items-start space-x-4 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0">
                  <service.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{service.title}</h3>
                  <p className="text-gray-600">{service.copy}</p>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="/services" className="text-green-600 hover:text-green-700 font-semibold border-b border-green-600">
              See everything we do →
            </a>
          </div>
        </div>
      </section>

      {/* Review */}
      {review && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex justify-center text-yellow-400 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-current" />
              ))}
            </div>
            <p className="text-xl text-gray-700 italic leading-relaxed mb-4">"{review.text}"</p>
            <p className="text-gray-500 font-medium">— {review.name}, Google Review</p>
          </div>
        </section>
      )}

      {/* Nearby areas */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">We Also Serve Nearby</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {nearby.map(spot => (
              <a
                key={spot.slug}
                href={`/tree-service/${spot.slug}`}
                className="bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-full font-medium transition-colors"
              >
                {spot.name}
              </a>
            ))}
            <a
              href="/contact"
              className="bg-white border border-gray-200 text-gray-600 hover:border-green-600 hover:text-green-700 px-4 py-2 rounded-full font-medium transition-colors"
            >
              All service areas
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Got a Tree Problem in {area.name}?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Free on-site assessment, honest answer, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14432080747"
              className="bg-white text-green-700 hover:bg-green-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Call (443) 208-0747
            </a>
            <button
              onClick={() => openQuoteModal()}
              className="border-2 border-white text-white hover:bg-white hover:text-green-700 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Get a Free Quote
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <QuoteModal />
    </div>
  );
};

export default ServiceAreaPage;
